/**
 * "Upload data" (TRA-1343): staff publish a team's trajectories as a set the
 * "Run training" dialog can train on, beside the keeper sets. The browser
 * reads both files and sends their text; the api checks every row, stores
 * the files byte for byte and refuses a set name that is taken. Its refusal
 * names the file and line at fault, so it shows here, under the form.
 *
 * A set too large for one upload is registered from the bucket on the
 * dialog's second tab (RegisterSetForm, TRA-1559).
 *
 * Opened from the Training page's data menu (TrainingDataMenu), which owns
 * whether it is open.
 */

import { Upload } from 'lucide-react'
import { useId, useState } from 'react'
import { toast } from 'sonner'

import { getApiErrorDetail } from '@/api/client'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useTrainingSetOptions, useUploadTrainingSet } from '@/hooks/useTrainingRuns'

import { RegisterSetForm } from './RegisterSetForm'
import { parseUploadSet, prepareUpload } from './upload'
import type { UploadSetErrors, UploadSetField } from './upload'

interface UploadSetDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function UploadSetDialog({ open, onOpenChange }: UploadSetDialogProps): React.JSX.Element {
  const [setId, setSetId] = useState('')
  const [license, setLicense] = useState('')
  const [train, setTrain] = useState<File | null>(null)
  const [validation, setValidation] = useState<File | null>(null)
  const [errors, setErrors] = useState<UploadSetErrors>({})
  const [refusal, setRefusal] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [mode, setMode] = useState<'upload' | 'bucket'>('upload')
  const upload = useUploadTrainingSet()
  // The team's staging folder for the bucket tab: the sets query the page has read already.
  const staging = useTrainingSetOptions(open).data?.staging
  const ids: Record<UploadSetField, string> = {
    setId: useId(),
    license: useId(),
    train: useId(),
    validation: useId(),
  }

  function reset(): void {
    setSetId('')
    setLicense('')
    setTrain(null)
    setValidation(null)
    setErrors({})
    setRefusal(null)
  }

  async function submit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const parsed = parseUploadSet({ setId, license, train, validation })
    if (parsed.kind === 'error') {
      setErrors(parsed.errors)
      return
    }
    setErrors({})
    setRefusal(null)
    setPending(true)
    // `pending` is released in the finally, so a failed read or request
    // never leaves the buttons disabled.
    try {
      const prepared = await prepareUpload(parsed.form)
      if (prepared.kind === 'error') {
        setRefusal(prepared.message)
        return
      }
      await upload.mutateAsync({ data: prepared.request })
      toast.success(`Data ${parsed.form.setId} is ready to train.`)
      onOpenChange(false)
      reset()
    } catch (caught) {
      setRefusal(getApiErrorDetail(caught) ?? 'The data was not uploaded. Try again.')
    } finally {
      setPending(false)
    }
  }

  const described = (field: UploadSetField): string | undefined =>
    errors[field] === undefined ? undefined : `${ids[field]}-error`

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (pending) return
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upload data</DialogTitle>
          <DialogDescription>
            {mode === 'upload'
              ? 'Upload the training file and the validation file of an SFT set. Each file holds one conversation per line. A line is a JSON object with the conversation\'s messages and tools. Name the terms the data may be trained under. Training stamps them on the dataset it registers. Uploaded data cannot change.'
              : 'Register an SFT set too large for one upload. Copy its training file and validation file into the team\'s staging folder and register them. Every row is checked where the files are. Published data cannot change.'}
          </DialogDescription>
        </DialogHeader>
        <Tabs
          value={mode}
          onValueChange={(next) => {
            if (!pending) setMode(next === 'bucket' ? 'bucket' : 'upload')
          }}
        >
          <TabsList>
            <TabsTrigger value="upload">Upload files</TabsTrigger>
            <TabsTrigger value="bucket">From the bucket</TabsTrigger>
          </TabsList>
          <TabsContent value="bucket">
            <RegisterSetForm
              staging={staging}
              onCancel={() => {
                onOpenChange(false)
              }}
            />
          </TabsContent>
          <TabsContent value="upload">
            <form onSubmit={(event) => void submit(event)} className="space-y-4" noValidate>
              <div className="space-y-2">
                <Label htmlFor={ids.setId}>Data name</Label>
                <Input
                  id={ids.setId}
                  value={setId}
                  placeholder="sft-set-2026-09-25"
                  className="font-mono"
                  disabled={pending}
                  aria-invalid={errors.setId !== undefined}
                  aria-describedby={described('setId')}
                  onChange={(event) => {
                    setSetId(event.target.value)
                  }}
                />
                <FieldError id={`${ids.setId}-error`} message={errors.setId} />
              </div>
              <div className="space-y-2">
                <Label htmlFor={ids.license}>License</Label>
                <Textarea
                  id={ids.license}
                  value={license}
                  rows={2}
                  placeholder="Private data. Training authorized by its owner."
                  disabled={pending}
                  aria-invalid={errors.license !== undefined}
                  aria-describedby={described('license')}
                  onChange={(event) => {
                    setLicense(event.target.value)
                  }}
                />
                <FieldError id={`${ids.license}-error`} message={errors.license} />
              </div>
              <div className="space-y-2">
                <Label htmlFor={ids.train}>Training file (.jsonl)</Label>
                <Input
                  id={ids.train}
                  type="file"
                  accept=".jsonl,application/jsonl"
                  disabled={pending}
                  aria-invalid={errors.train !== undefined}
                  aria-describedby={described('train')}
                  onChange={(event) => {
                    setTrain(event.target.files?.[0] ?? null)
                  }}
                />
                <FieldError id={`${ids.train}-error`} message={errors.train} />
              </div>
              <div className="space-y-2">
                <Label htmlFor={ids.validation}>Validation file (.jsonl)</Label>
                <Input
                  id={ids.validation}
                  type="file"
                  accept=".jsonl,application/jsonl"
                  disabled={pending}
                  aria-invalid={errors.validation !== undefined}
                  aria-describedby={described('validation')}
                  onChange={(event) => {
                    setValidation(event.target.files?.[0] ?? null)
                  }}
                />
                <FieldError id={`${ids.validation}-error`} message={errors.validation} />
              </div>
              {refusal !== null && (
                <p role="alert" className="text-sm text-destructive">
                  {refusal}
                </p>
              )}
              <DialogFooter className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={pending}
                  onClick={() => {
                    onOpenChange(false)
                    reset()
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={pending}>
                  <Upload className="mr-2 h-4 w-4" />
                  {pending ? 'Uploading…' : 'Upload data'}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
